/**
 * Advanced Animations - GSAP-powered premium animations
 */

class AdvancedAnimations {
  constructor() {
    this.init();
  }

  init() {
    this.waitForGSAP().then(() => {
      this.setupScrollTrigger();
      this.initTextAnimations();
      this.initParallaxEffects();
      this.initMagneticButtons();
      this.initRevealAnimations();
      this.initGlowEffects();
      this.initFloatingElements();
    });
  }

  waitForGSAP() {
    return new Promise((resolve) => {
      if (window.gsap) {
        resolve();
        return;
      }
      
      const checkGSAP = setInterval(() => {
        if (window.gsap) {
          clearInterval(checkGSAP);
          resolve();
        }
      }, 100);
    });
  }

  setupScrollTrigger() {
    // Register ScrollTrigger if available
    if (window.ScrollTrigger) {
      gsap.registerPlugin(ScrollTrigger);
    }
  }

  initTextAnimations() {
    // Animate gradient text on scroll
    const gradientTexts = document.querySelectorAll('.gradient-text');
    
    gradientTexts.forEach(text => {
      gsap.fromTo(text, 
        { 
          backgroundPosition: '0% 50%',
          opacity: 0,
          y: 30
        },
        {
          backgroundPosition: '100% 50%',
          opacity: 1,
          y: 0,
          duration: 1.5,
          ease: 'power3.out',
          scrollTrigger: window.ScrollTrigger ? {
            trigger: text,
            start: 'top 80%',
            toggleActions: 'play none none reverse'
          } : null
        }
      );
    });

    // Split text animation for headings
    const headings = document.querySelectorAll('h1, h2');
    headings.forEach(heading => {
      if (!heading.classList.contains('split-animated')) {
        heading.classList.add('split-animated');
      }
    });
  }

  initParallaxEffects() {
    const parallaxElements = document.querySelectorAll('.tech-badge, .glass-card');
    
    parallaxElements.forEach((el, index) => {
      const speed = 0.5 + (index % 3) * 0.2;
      
      gsap.to(el, {
        y: () => window.scrollY * speed * 0.1,
        ease: 'none',
        scrollTrigger: window.ScrollTrigger ? {
          trigger: el,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true
        } : null
      });
    });

    // Mouse parallax for hero section
    const heroSection = document.getElementById('home');
    if (heroSection) {
      heroSection.addEventListener('mousemove', (e) => {
        const rect = heroSection.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width - 0.5;
        const y = (e.clientY - rect.top) / rect.height - 0.5;
        
        const parallaxItems = heroSection.querySelectorAll('.tech-badge');
        parallaxItems.forEach((item, index) => {
          const depth = 1 + (index % 5) * 0.5;
          gsap.to(item, {
            x: x * 20 * depth,
            y: y * 20 * depth,
            duration: 0.5,
            ease: 'power2.out'
          });
        });
      });
    }
  }

  initMagneticButtons() {
    const buttons = document.querySelectorAll('.glow-button, .glass-button, .outline-button');
    
    buttons.forEach(button => {
      button.addEventListener('mousemove', (e) => {
        const rect = button.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        
        gsap.to(button, {
          x: x * 0.3,
          y: y * 0.3,
          duration: 0.3,
          ease: 'power2.out'
        });
      });
      
      button.addEventListener('mouseleave', () => {
        gsap.to(button, {
          x: 0,
          y: 0,
          duration: 0.5,
          ease: 'elastic.out(1, 0.5)'
        });
      });
    });
  }

  initRevealAnimations() {
    // Stagger reveal for skill cards
    const skillCards = document.querySelectorAll('.skill-card');
    
    if (skillCards.length > 0) {
      gsap.fromTo(skillCards,
        {
          opacity: 0,
          y: 50,
          scale: 0.9
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.8,
          stagger: 0.15,
          ease: 'power3.out',
          scrollTrigger: window.ScrollTrigger ? {
            trigger: skillCards[0].parentElement,
            start: 'top 70%',
            toggleActions: 'play none none reverse'
          } : null
        }
      );
    }

    // Tech pills reveal
    const techPills = document.querySelectorAll('.tech-pill');
    
    if (techPills.length > 0) {
      gsap.fromTo(techPills,
        {
          opacity: 0,
          scale: 0,
          rotation: -10
        },
        {
          opacity: 1,
          scale: 1,
          rotation: 0,
          duration: 0.5,
          stagger: 0.03,
          ease: 'back.out(2)',
          scrollTrigger: window.ScrollTrigger ? {
            trigger: techPills[0].closest('section'),
            start: 'top 60%',
            toggleActions: 'play none none reverse'
          } : null
        }
      );
    }

    // Tech badges reveal with bounce
    const techBadges = document.querySelectorAll('.tech-badge');
    
    if (techBadges.length > 0) {
      gsap.fromTo(techBadges,
        {
          opacity: 0,
          y: 30,
          scale: 0
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.6,
          stagger: 0.05,
          ease: 'back.out(1.7)',
          delay: 1.5 // After page load animation
        }
      );
    }
  }

  initGlowEffects() {
    // Dynamic glow on cards
    const glowCards = document.querySelectorAll('.glass-card, .project-card');
    
    glowCards.forEach(card => {
      card.addEventListener('mousemove', (e) => {
        const rect = card.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        
        card.style.setProperty('--glow-x', `${x}%`);
        card.style.setProperty('--glow-y', `${y}%`);
      });
    });
  }

  initFloatingElements() {
    // Continuous floating animation for specific elements
    const floatingElements = document.querySelectorAll('.floating-element');
    
    floatingElements.forEach((el, index) => {
      const duration = 3 + Math.random() * 2;
      const delay = index * 0.2;
      
      gsap.to(el, {
        y: '+=15',
        duration: duration,
        delay: delay,
        ease: 'sine.inOut',
        yoyo: true,
        repeat: -1
      });
      
      gsap.to(el, {
        x: '+=10',
        duration: duration * 1.3,
        delay: delay,
        ease: 'sine.inOut',
        yoyo: true,
        repeat: -1
      });
    });
  }
}

// Smooth scroll enhancement
class SmoothScroll {
  constructor() {
    this.init();
  }

  init() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', (e) => {
        e.preventDefault();
        const target = document.querySelector(anchor.getAttribute('href'));
        
        if (target && window.gsap) {
          gsap.to(window, {
            duration: 1.2,
            scrollTo: { y: target, offsetY: 70 },
            ease: 'power3.inOut'
          });
        } else if (target) {
          target.scrollIntoView({ behavior: 'smooth' });
        }
      });
    });
  }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
  window.advancedAnimations = new AdvancedAnimations();
  window.smoothScroll = new SmoothScroll();
});
